import { EmailData } from './emailService';
import { sendToGoogleSheets, formatProductsForSheets, GoogleSheetsData } from '@/utils/googleSheets';
import { reservationLogger } from '@/utils/logger';

/**
 * Service for handling reservation data persistence
 */
export class ReservationService {
  /**
   * Get display name for château type
   */
  private static getChateauDisplayName(id: string, chateaux?: any[]): string {
    if (chateaux) {
      const dbChateau = chateaux.find(c => c.id === id);
      if (dbChateau) {
        return dbChateau.nom;
      }
    }
    
    const names: { [key: string]: string } = {
      "disco-m": "Château Disco M",
      "grenouille": "Château Grenouille", 
      "disco-lm": "Château Disco LM",
      "twister": "Château Twister",
      "dino": "Château Dino",
      "vache": "Château Vache",
      "princesse": "Château Princesse",
      "pingouin": "Château Pingouin",
      "Standard": "Château Standard",
      "Tapis": "Tapis Gonflable",
      "sumos": "Sumos Gonflables"
    };
    return names[id] || id.charAt(0).toUpperCase() + id.slice(1);
  }

  /**
   * Save reservation to Google Sheets
   */
  static async saveToGoogleSheets(data: EmailData, chateaux?: any[]): Promise<boolean> {
    reservationLogger.info("Début envoi vers Google Sheets");
    
    try {
      const sheetsData: GoogleSheetsData = {
        timestamp: new Date().toISOString(),
        nom: data.name,
        email: data.email,
        telephone: data.phone,
        date_evenement: new Date(data.eventDate).toLocaleDateString('fr-FR'),
        produits: formatProductsForSheets(
          data.products, 
          data.chateauType, 
          (id: string) => this.getChateauDisplayName(id, chateaux)
        ),
        chateau_type: data.chateauType ? this.getChateauDisplayName(data.chateauType, chateaux) : '',
        message: data.message || '',
        source: 'Formulaire Website (ReservationForm)'
      };

      await sendToGoogleSheets(sheetsData);
      reservationLogger.info("Google Sheets sauvegardé avec succès");
      return true;

    } catch (error) {
      reservationLogger.error("Google Sheets backup échoué", error);
      return false;
    }
  }

  /**
   * Get estimated price for the reservation
   */
  static getEstimatedPrice(data: any): number {
    return data.products.chateau ? 90 : 50;
  }

  /**
   * Get product type for tracking
   */
  static getProductType(data: any, chateaux?: any[]): string {
    if (data.products.chateau) {
      return data.chateauType ? this.getChateauDisplayName(data.chateauType, chateaux) : 'Château Gonflable';
    }
    return 'Sumo';
  }

  /**
   * Get château options for form dropdown
   */
  static getChateauOptions(chateaux: any[]): Array<{ id: string; name: string }> {
    if (chateaux.length > 0) {
      return chateaux.filter(c => c.is_visible).map(c => ({ id: c.id, name: c.nom }));
    }
    
    // Fallback options
    return [
      { id: "pingouin", name: "Pingouin" },
      { id: "dino", name: "Dino" },
      { id: "vache", name: "Vache" },
      { id: "twister", name: "Twister" },
      { id: "princesse", name: "Princesse" },
      { id: "grenouille", name: "Grenouille" },
      { id: "disco", name: "Disco" },
      { id: "standard", name: "Standard" },
      { id: "tapis", name: "Tapis" },
    ];
  }
}